// =============================================
// SeoMetaPanel — etap PRZED publikacją.
// Tabela ready nodów z aktualnym seo_title + meta_description + przyciski:
//   - "Generuj" per row (gpt-4.1)
//   - "Generuj wszystkie brakujące" batch
// Edycja inline (textarea) + save manual.
//
// Po Generuj/Save dane idą do thematic_node_content.{seo_title, meta_description}.
// publishQueueService czyta te pola automatycznie przy publikacji do WP.
// =============================================

import { useEffect, useMemo, useState } from "react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Badge } from "@/components/ui/badge";
import { Alert, AlertDescription } from "@/components/ui/alert";
import { Sparkles, Save, Loader2, RefreshCw, Wand2, Info, AlertTriangle } from "lucide-react";
import { useToast } from "@/hooks/use-toast";
import { supabase } from "@/integrations/supabase/client";
import {
  generateSeoMeta,
  batchGenerateSeoMeta,
  type SeoMetaResult,
} from "@/services/seoMetaGeneratorService";

interface SeoMetaPanelProps {
  mapId: string;
}

interface Row {
  node_id: string;
  node_title: string;
  document_role: string | null;
  engine_stage: string | null;
  seo_title: string | null;
  meta_description: string | null;
  draft_seo_title: string;
  draft_meta_description: string;
  dirty: boolean;
  generating: boolean;
  warnings: string[];
  rationale: string;
}

function lenColor(len: number, min: number, max: number): string {
  if (len === 0) return "text-muted-foreground";
  if (len < min) return "text-amber-500";
  if (len > max) return "text-red-500";
  return "text-emerald-500";
}

export function SeoMetaPanel({ mapId }: SeoMetaPanelProps) {
  const { toast } = useToast();
  const [rows, setRows] = useState<Row[]>([]);
  const [loading, setLoading] = useState(true);
  const [batchRunning, setBatchRunning] = useState(false);
  const [batchProgress, setBatchProgress] = useState<{ done: number; total: number; failed: number }>({
    done: 0,
    total: 0,
    failed: 0,
  });

  const loadRows = async () => {
    setLoading(true);
    try {
      const { data: nodes } = await (supabase as any)
        .from("thematic_nodes")
        .select("id, title, document_role")
        .eq("map_id", mapId)
        .order("sort_order", { ascending: true });
      const nodeIds = (nodes || []).map((n: { id: string }) => n.id);
      if (nodeIds.length === 0) {
        setRows([]);
        return;
      }
      const { data: contents } = await (supabase as any)
        .from("thematic_node_content")
        .select("node_id, seo_title, meta_description, engine_stage")
        .in("node_id", nodeIds);
      const contentMap = new Map(
        (contents || []).map((c: { node_id: string }) => [c.node_id, c]),
      );
      const next: Row[] = (nodes || [])
        .map((n: { id: string; title: string; document_role: string | null }) => {
          const c = contentMap.get(n.id) as {
            seo_title: string | null;
            meta_description: string | null;
            engine_stage: string | null;
          } | undefined;
          if (!c) return null;
          // Pokazujemy tylko ready / published (po Etap 2)
          if (c.engine_stage !== "ready" && c.engine_stage !== "published") return null;
          return {
            node_id: n.id,
            node_title: n.title,
            document_role: n.document_role,
            engine_stage: c.engine_stage,
            seo_title: c.seo_title,
            meta_description: c.meta_description,
            draft_seo_title: c.seo_title || "",
            draft_meta_description: c.meta_description || "",
            dirty: false,
            generating: false,
            warnings: [],
            rationale: "",
          } as Row;
        })
        .filter((r: Row | null): r is Row => r !== null);
      setRows(next);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadRows();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [mapId]);

  const missingCount = useMemo(
    () => rows.filter((r) => !r.seo_title || !r.meta_description).length,
    [rows],
  );

  const updateRow = (nodeId: string, patch: Partial<Row>) => {
    setRows((prev) => prev.map((r) => (r.node_id === nodeId ? { ...r, ...patch } : r)));
  };

  const handleGenerate = async (nodeId: string) => {
    updateRow(nodeId, { generating: true });
    try {
      const result: SeoMetaResult = await generateSeoMeta(nodeId);
      updateRow(nodeId, {
        seo_title: result.seo_title,
        meta_description: result.meta_description,
        draft_seo_title: result.seo_title,
        draft_meta_description: result.meta_description,
        dirty: false,
        generating: false,
        warnings: result.warnings,
        rationale: result.rationale,
      });
      toast({
        title: "Wygenerowano SEO meta",
        description: `Title ${result.seo_title.length}ch / Desc ${result.meta_description.length}ch / $${result.cost_usd.toFixed(4)}`,
      });
    } catch (e) {
      updateRow(nodeId, { generating: false });
      toast({
        title: "Błąd generowania",
        description: e instanceof Error ? e.message : String(e),
        variant: "destructive",
      });
    }
  };

  const handleSaveManual = async (nodeId: string) => {
    const row = rows.find((r) => r.node_id === nodeId);
    if (!row) return;
    updateRow(nodeId, { generating: true });
    try {
      const { error } = await (supabase as any)
        .from("thematic_node_content")
        .update({
          seo_title: row.draft_seo_title || null,
          meta_description: row.draft_meta_description || null,
          updated_at: new Date().toISOString(),
        })
        .eq("node_id", nodeId);
      if (error) throw error;
      updateRow(nodeId, {
        seo_title: row.draft_seo_title,
        meta_description: row.draft_meta_description,
        dirty: false,
        generating: false,
        warnings: [],
      });
      toast({ title: "Zapisano", description: "Manual edit zapisany" });
    } catch (e) {
      updateRow(nodeId, { generating: false });
      toast({
        title: "Błąd zapisu",
        description: e instanceof Error ? e.message : String(e),
        variant: "destructive",
      });
    }
  };

  const handleBatchGenerate = async () => {
    setBatchRunning(true);
    const targets = rows.filter((r) => !r.seo_title || !r.meta_description);
    setBatchProgress({ done: 0, total: targets.length, failed: 0 });
    try {
      await batchGenerateSeoMeta(mapId, {
        onlyMissing: true,
        onProgress: (p) => {
          if (p.status === "ok" && p.result) {
            updateRow(p.nodeId, {
              seo_title: p.result.seo_title,
              meta_description: p.result.meta_description,
              draft_seo_title: p.result.seo_title,
              draft_meta_description: p.result.meta_description,
              dirty: false,
              warnings: p.result.warnings,
              rationale: p.result.rationale,
            });
            setBatchProgress((prev) => ({ ...prev, done: prev.done + 1 }));
          } else {
            setBatchProgress((prev) => ({ ...prev, done: prev.done + 1, failed: prev.failed + 1 }));
          }
        },
      });
      toast({ title: "Batch zakończony", description: `${targets.length} nodów przetworzone` });
    } catch (e) {
      toast({
        title: "Batch failed",
        description: e instanceof Error ? e.message : String(e),
        variant: "destructive",
      });
    } finally {
      setBatchRunning(false);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center p-8 text-muted-foreground text-sm">
        <Loader2 className="h-4 w-4 animate-spin mr-2" />
        Ładowanie nodów...
      </div>
    );
  }

  if (rows.length === 0) {
    return (
      <Alert>
        <Info className="h-4 w-4" />
        <AlertDescription className="text-xs">
          Brak nodów ready/published do generowania SEO meta. Najpierw wygeneruj treść (Etap 2).
        </AlertDescription>
      </Alert>
    );
  }

  return (
    <div className="space-y-3">
      <div className="flex items-center justify-between gap-2 rounded-md border border-indigo-500/30 bg-indigo-500/5 p-3">
        <div className="flex items-start gap-2">
          <Sparkles className="h-4 w-4 text-indigo-500 mt-0.5 shrink-0" />
          <div>
            <h3 className="text-sm font-semibold">Krok 3 — SEO meta przed publikacją</h3>
            <p className="text-[11px] text-muted-foreground mt-0.5">
              Generuje SEO title (50-65 ch) + meta description (140-170 ch) przez gpt-4.1.
              Wynik trafia do <code>thematic_node_content.seo_title / meta_description</code> i przy
              publikacji idzie do:
              <span className="block ml-3 mt-0.5">
                • <strong>Yoast SEO title</strong> (<code>_yoast_wpseo_title</code>) → tag <code>&lt;title&gt;</code> + og:title
              </span>
              <span className="block ml-3">
                • <strong>Yoast meta description</strong> (<code>_yoast_wpseo_metadesc</code>) → tag <code>&lt;meta name="description"&gt;</code>
              </span>
              <span className="block ml-3">
                • <strong>Schema.org headline</strong> w JSON-LD <code>@graph</code>
              </span>
              <span className="block mt-1 text-amber-500">
                ⚠ WP post title (H1 na stronie) zostaje ORYGINALNY — generator NIE nadpisuje treści artykułu.
              </span>
            </p>
            <p className="text-[10px] text-muted-foreground mt-1">
              Sugerowany krok: <strong>{missingCount} z {rows.length} bez seo meta</strong>. Kliknij
              "Generuj brakujące" lub per row.
            </p>
          </div>
        </div>
        <div className="flex items-center gap-2 shrink-0">
          <Button size="sm" variant="outline" onClick={loadRows} disabled={batchRunning} className="h-8">
            <RefreshCw className="h-3.5 w-3.5 mr-1.5" />
            Odśwież
          </Button>
          <Button
            size="sm"
            onClick={handleBatchGenerate}
            disabled={batchRunning || missingCount === 0}
            className="h-8"
          >
            {batchRunning ? (
              <Loader2 className="h-3.5 w-3.5 mr-1.5 animate-spin" />
            ) : (
              <Wand2 className="h-3.5 w-3.5 mr-1.5" />
            )}
            Generuj brakujące ({missingCount})
          </Button>
        </div>
      </div>

      {batchRunning && (
        <Alert>
          <Loader2 className="h-4 w-4 animate-spin" />
          <AlertDescription className="text-xs">
            Batch w toku: {batchProgress.done} / {batchProgress.total}
            {batchProgress.failed > 0 && (
              <span className="text-red-500"> ({batchProgress.failed} błędów)</span>
            )}
          </AlertDescription>
        </Alert>
      )}

      <div className="rounded-md border border-border/60 divide-y divide-border/40">
        <div className="grid grid-cols-[1fr_auto] gap-2 px-3 py-2 bg-muted/30 text-[10px] font-semibold uppercase tracking-wide text-muted-foreground">
          <div>Node / SEO Title / Meta Description</div>
          <div className="text-right">Akcje</div>
        </div>
        {rows.map((row) => {
          const titleLen = row.draft_seo_title.length;
          const descLen = row.draft_meta_description.length;
          return (
            <div key={row.node_id} className="grid grid-cols-[1fr_auto] gap-2 px-3 py-3 hover:bg-muted/10">
              <div className="space-y-2 min-w-0">
                <div className="flex items-center gap-2">
                  <span className="text-xs font-medium truncate" title={row.node_title}>
                    {row.node_title}
                  </span>
                  <Badge variant="outline" className="text-[9px] shrink-0">
                    {row.document_role || "?"}
                  </Badge>
                  <Badge variant="outline" className="text-[9px] shrink-0">
                    {row.engine_stage}
                  </Badge>
                  {row.seo_title && row.meta_description ? (
                    <Badge className="text-[9px] bg-emerald-500/15 text-emerald-600 dark:text-emerald-400 border-emerald-500/30 shrink-0">
                      OK
                    </Badge>
                  ) : (
                    <Badge variant="outline" className="text-[9px] text-amber-500 border-amber-500/30 shrink-0">
                      brak
                    </Badge>
                  )}
                </div>

                <div className="space-y-1.5">
                  <div className="flex items-center gap-2">
                    <span className="text-[10px] text-muted-foreground w-24 shrink-0">SEO title</span>
                    <Input
                      value={row.draft_seo_title}
                      onChange={(e) =>
                        updateRow(row.node_id, { draft_seo_title: e.target.value, dirty: true })
                      }
                      placeholder="Wygenerowany SEO title pojawi się tutaj..."
                      className="h-7 text-xs"
                    />
                    <span className={`text-[10px] w-12 text-right shrink-0 ${lenColor(titleLen, 30, 70)}`}>
                      {titleLen}/65
                    </span>
                  </div>
                  <div className="flex items-start gap-2">
                    <span className="text-[10px] text-muted-foreground w-24 shrink-0 pt-2">Meta desc</span>
                    <Textarea
                      value={row.draft_meta_description}
                      onChange={(e) =>
                        updateRow(row.node_id, {
                          draft_meta_description: e.target.value,
                          dirty: true,
                        })
                      }
                      placeholder="Wygenerowany meta description pojawi się tutaj..."
                      rows={2}
                      className="text-xs resize-none"
                    />
                    <span
                      className={`text-[10px] w-12 text-right shrink-0 pt-2 ${lenColor(descLen, 120, 170)}`}
                    >
                      {descLen}/150
                    </span>
                  </div>
                </div>

                {row.rationale && (
                  <p className="text-[10px] text-muted-foreground italic line-clamp-2">
                    💡 {row.rationale}
                  </p>
                )}
                {row.warnings.length > 0 && (
                  <div className="flex items-start gap-1.5 text-[10px] text-amber-600 dark:text-amber-400">
                    <AlertTriangle className="h-3 w-3 mt-0.5 shrink-0" />
                    <span>{row.warnings.join(" · ")}</span>
                  </div>
                )}
              </div>

              <div className="flex flex-col items-end gap-1 shrink-0">
                <Button
                  size="sm"
                  variant="outline"
                  onClick={() => handleGenerate(row.node_id)}
                  disabled={row.generating || batchRunning}
                  className="h-7 text-xs w-32"
                >
                  {row.generating ? (
                    <Loader2 className="h-3 w-3 mr-1 animate-spin" />
                  ) : (
                    <Sparkles className="h-3 w-3 mr-1" />
                  )}
                  Generuj AI
                </Button>
                <Button
                  size="sm"
                  variant={row.dirty ? "default" : "ghost"}
                  onClick={() => handleSaveManual(row.node_id)}
                  disabled={!row.dirty || row.generating || batchRunning}
                  className="h-7 text-xs w-32"
                >
                  <Save className="h-3 w-3 mr-1" />
                  {row.dirty ? "Zapisz" : "Zapisano"}
                </Button>
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
}
